import React from 'react'
import moment from 'moment'
import { wallClockOf } from './flightNervePure'
import { displayTerminal } from 'helpers/terminalDisplay'
import {
  FLIGHT_CARD_STATUS,
  flightCardBadge,
  flightCardLabels,
  flightCardNote,
  flightCardProgress,
  flightCardTimeTone,
  getFlightCardStatus,
} from './flightCardStatus'

// "Tue 13 Feb": the day shown next to a city. UTC on purpose — flight dates are the airport's
// local wall clock written as UTC.
const dayText = value => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const weekday = date.toLocaleString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  })
  const month = date.toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  })
  return `${weekday} ${date.getUTCDate()} ${month}`
}

const clockText = value => {
  if (!value) return null
  const m = moment.utc(value)
  return m.isValid() ? m.format('HH:mm') : null
}

const shownClock = clock =>
  clock && clock.time
    ? `${clock.time.hours}:${clock.time.scheduleMinutes}`
    : null

// The route line: a track, the part flown, and the plane. A plane icon rather than an image so it
// takes the status colour.
const PlaneIcon = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    viewBox='0 0 24 24'
    width='19'
    height='19'
    aria-hidden='true'
    focusable='false'
  >
    <path
      transform='rotate(90 12 12)'
      d='M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z'
      fill='currentColor'
    />
  </svg>
)

// One side of the card: the place and day, then the time with its label, terminal and gate.
const Side = ({
  place,
  day,
  label,
  time,
  timeTone,
  struck,
  subnote,
  terminal,
  gate,
}) => (
  <div className='fsc-side'>
    <div className='fsc-place'>
      {place}
      <span className='fsc-dot'>•</span>
      {day}
    </div>
    <div className='fsc-facts'>
      <div className='fsc-fact'>
        <span className='fsc-label'>{label}</span>
        <span className={`fsc-value fsc-value--${timeTone}`}>{time}</span>
        {struck ? <span className='fsc-struck'>{struck}</span> : null}
        {subnote ? <span className='fsc-subnote'>{subnote}</span> : null}
      </div>
      <div className='fsc-fact'>
        <span className='fsc-label'>TERMINAL</span>
        <span className='fsc-value'>{terminal || '-'}</span>
      </div>
      <div className='fsc-fact'>
        <span className='fsc-label'>GATE</span>
        <span className='fsc-value'>{gate || '-'}</span>
      </div>
    </div>
  </div>
)

const FlightTrackingCard = ({
  getCity,
  departureTime,
  arrivalTime,
  delayDepartureTime,
  airportPickUpInfo,
  airportDropOffInfo,
  flightInfo,
  flightTotalDuration,
  progressPercentage,
  delayArrivalTime,
  noData,
  time,
  flightDataLoading,
  index,
  numberOfFlights,
  // RD-4141: true inside the dropdown (the header above already carries the status badge); false
  // for the card shown on its own (VIP Meet & Greet, partner), which then carries its own badge.
  hasHeader = false,
}) => {
  // A booking's date (airportPickUpInfo.date / airportDropOffInfo.date) may stand in for the
  // flight's own date ONLY on the same calendar day, or when the flight has no date.
  // Pinning it unconditionally invented flights: 5625-V (pick-up 20 Oct, AC866) showed
  // "YUL 19 Oct 18:40 → LHR 20 Oct 06:30", but AC866 does not fly on 19 Oct — the flight
  // found was the 20 Oct one landing on the 21st, re-labelled with the pick-up date.
  const sameCalendarDay = (a, b) => {
    const x = wallClockOf(a)
    const y = wallClockOf(b)
    return Boolean(x && y && x.day === y.day)
  }
  const bookedDateIfSameDay = (bookedValue, flightValue) => {
    if (!flightValue) return bookedValue || null
    if (!bookedValue) return flightValue
    return sameCalendarDay(bookedValue, flightValue) ? bookedValue : flightValue
  }
  const arrivalDateValue = bookedDateIfSameDay(
    airportPickUpInfo?.date,
    time?.arrival
  )
  // The departure leg's own `time.departure` DATE can be stale when the pickup date is
  // changed (it is fetched/cached separately). To keep the two legs consistent we anchor the
  // departure DATE to the arrival date and only apply the genuine whole-day offset between
  // the flight's scheduled departure and arrival (same fetch, so internally consistent).
  // Time-of-day is rendered separately and is unaffected.
  const getDepartureDateValue = () => {
    // Drop-off transfers carry their own departure date — trusted on the same terms.
    if (airportDropOffInfo?.date) {
      return bookedDateIfSameDay(airportDropOffInfo.date, time?.departure)
    }
    if (arrivalDateValue && time?.arrival && time?.departure) {
      const arr = new Date(time.arrival)
      const dep = new Date(time.departure)
      const anchor = new Date(arrivalDateValue)
      if (!isNaN(arr) && !isNaN(dep) && !isNaN(anchor)) {
        const arrDay = Date.UTC(
          arr.getUTCFullYear(),
          arr.getUTCMonth(),
          arr.getUTCDate()
        )
        const depDay = Date.UTC(
          dep.getUTCFullYear(),
          dep.getUTCMonth(),
          dep.getUTCDate()
        )
        const dayDiff = Math.round((arrDay - depDay) / 86400000)
        if (dayDiff !== 0) {
          anchor.setUTCDate(anchor.getUTCDate() - dayDiff)
        }
        return anchor.toISOString()
      }
    }
    return time?.departure
  }
  const departureDateValue = getDepartureDateValue()
  if (flightDataLoading || noData || !flightInfo) return null

  // RD-4141: the card's look is decided by one status (see flightCardStatus.js).
  const status = getFlightCardStatus({
    flightInfo,
    delayDepartureTime,
    delayArrivalTime,
    progressPercentage,
  })
  const badge = flightCardBadge(status)
  const progress = flightCardProgress(status, progressPercentage)
  const labels = flightCardLabels(status, progress)
  const timeTone = flightCardTimeTone(status)
  const awayFromPlan =
    status === FLIGHT_CARD_STATUS.DIVERTED ||
    status === FLIGHT_CARD_STATUS.RETURNED
  const tone = status === FLIGHT_CARD_STATUS.CANCELLED ? 'red' : badge.tone

  const departureCity =
    getCity && getCity.departureInfo ? getCity.departureInfo.city : ''
  const arrivalCity =
    getCity && getCity.arrivalInfo ? getCity.arrivalInfo.city : ''
  const departureIata = flightInfo.departure && flightInfo.departure.iata
  const arrivalIata = flightInfo.arrival && flightInfo.arrival.iata

  // The airport the booking expects the flight at. Only meaningful when the flight is somewhere else.
  const expectedIata = airportPickUpInfo && airportPickUpInfo.iata
  const wentElsewhere =
    awayFromPlan &&
    expectedIata &&
    String(expectedIata).toUpperCase() !==
      String(arrivalIata || '').toUpperCase()
  const note = wentElsewhere ? flightCardNote(status, { expectedIata }) : null

  // The scheduled time, struck through under the time shown whenever the two differ.
  const departureShown = shownClock(departureTime)
  const arrivalShown = shownClock(arrivalTime)
  const departureScheduled = clockText(time && time.departure)
  const arrivalScheduled = clockText(time && time.arrival)
  const departureStruck =
    departureScheduled &&
    departureShown &&
    departureScheduled !== departureShown
      ? departureScheduled
      : null
  const arrivalStruck =
    !awayFromPlan &&
    arrivalScheduled &&
    arrivalShown &&
    arrivalScheduled !== arrivalShown
      ? arrivalScheduled
      : null
  const wasDue =
    wentElsewhere && arrivalScheduled
      ? `Was due ${String(expectedIata).toUpperCase()} ${arrivalScheduled}`
      : null

  const title = `${
    flightInfo.airline && flightInfo.airline.name ? flightInfo.airline.name : ''
  } ${flightInfo.airline ? flightInfo.airline.iata : ''} ${
    flightInfo.airline ? flightInfo.airline.number : ''
  }`.trim()
  const routeNames =
    status === FLIGHT_CARD_STATUS.RETURNED
      ? `Returned to ${departureCity}`
      : `${departureCity} to ${arrivalCity}`

  const isLast = index === numberOfFlights - 1
  // Figma (dropdown variant): Landed and Cancelled repeat the badge in the body under the header's.
  const keepsBodyBadge =
    status === FLIGHT_CARD_STATUS.LANDED ||
    status === FLIGHT_CARD_STATUS.CANCELLED

  return (
    <div
      className={`flight-status-card flight-status-card--${status} ${
        hasHeader ? 'flight-status-card--with-header' : ''
      } ${isLast ? 'flight-border-bottom' : ''}`}
    >
      <div className='fsc-top'>
        <div className='fsc-heading'>
          <div className='fsc-title'>{title}</div>
          {departureCity && arrivalCity ? (
            <div className='fsc-route-names'>{routeNames}</div>
          ) : null}
          {note ? <div className='fsc-note'>{note}</div> : null}
        </div>
        {hasHeader && !keepsBodyBadge ? null : (
          <span className={`fsc-badge fsc-badge--${tone}`}>{badge.label}</span>
        )}
      </div>

      <div className='fsc-route'>
        <span className='fsc-code'>{departureIata}</span>
        <div className={`fsc-line fsc-line--${tone}`}>
          {(status === FLIGHT_CARD_STATUS.SCHEDULED ||
            status === FLIGHT_CARD_STATUS.CANCELLED) &&
          flightTotalDuration ? (
            <span className='fsc-duration'>{flightTotalDuration}</span>
          ) : null}
          <div className='fsc-track' />
          <div className='fsc-fill' style={{ width: `${progress}%` }} />
          <PlaneIcon
            className='fsc-plane'
            style={{
              left: `${progress}%`,
              transform: `translate(-${progress}%, -50%)`,
            }}
          />
        </div>
        <span className='fsc-code fsc-code--end'>
          {status === FLIGHT_CARD_STATUS.RETURNED ? departureIata : arrivalIata}
        </span>
      </div>

      <div className='fsc-columns'>
        <Side
          place={departureCity}
          day={departureDateValue ? dayText(departureDateValue) : null}
          label={labels.departure}
          time={departureShown}
          timeTone={timeTone}
          struck={departureStruck}
          terminal={
            (airportDropOffInfo && airportDropOffInfo.terminal) ||
            displayTerminal(
              flightInfo.departure && flightInfo.departure.terminal
            )
          }
          gate={
            flightInfo.departure &&
            flightInfo.departure.gate &&
            flightInfo.departure.gate !== 'null'
              ? flightInfo.departure.gate
              : null
          }
        />
        <Side
          place={
            status === FLIGHT_CARD_STATUS.RETURNED ? departureCity : arrivalCity
          }
          day={arrivalDateValue ? dayText(arrivalDateValue) : null}
          label={labels.arrival}
          time={arrivalShown}
          timeTone={timeTone}
          struck={arrivalStruck}
          subnote={wasDue}
          terminal={
            (airportPickUpInfo && airportPickUpInfo.terminal) ||
            displayTerminal(flightInfo.arrival && flightInfo.arrival.terminal)
          }
          gate={
            flightInfo.arrival &&
            flightInfo.arrival.gate &&
            flightInfo.arrival.gate !== 'null'
              ? flightInfo.arrival.gate
              : null
          }
        />
      </div>
    </div>
  )
}

export default FlightTrackingCard
